import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, Button, Spinner } from '../../components/ui';
import { BASE, SrConsent, keys } from './api';

export function useConsent(version: number) {
  return useQuery({ queryKey: keys.consent(version), queryFn: () => api.get(`${BASE}/consent/${version}`, { schema: SrConsent }) });
}

export const agreementPath = (version: number): string => `/manage/shared-reception/agreement/${version}`;

/** Copy button for the suggested privacy-notice lines. */
function CopyLines({ lines }: { lines: string[] }) {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join('\n\n'));
      setState('ok');
    } catch {
      setState('fail');
    }
  };
  return (
    <div className="mt-3">
      <Button onClick={() => void copy()}>{t('srt.consent.copy')}</Button>
      <span role="status" className="ml-3 text-sm text-slate-700">
        {state === 'ok' ? t('srt.consent.copied') : state === 'fail' ? t('srt.consent.copyFailed') : ''}
      </span>
    </div>
  );
}

/**
 * The sharing statement a company reads before it accepts: the statement, the "what is shared" list, what is never shared,
 * a link to the (draft) data-sharing agreement and the suggested privacy-notice lines. Text comes from the server (GET /consent/:version).
 */
export function ConsentPanel({ version }: { version: number }) {
  const q = useConsent(version);
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const c = q.data;
  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{c.title}</h3>
        <p className="text-xs text-slate-600">{t('srt.consent.version', { version: c.version })}</p>
      </div>
      <div className="space-y-2 text-sm text-slate-800">
        {c.body.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      <section aria-labelledby={`shared-${version}`} className="rounded-lg border border-slate-200 bg-white p-4">
        <h4 id={`shared-${version}`} className="text-sm font-semibold text-slate-900">
          {t('srt.consent.shared.title')}
        </h4>
        <p className="text-xs text-slate-600">{t('srt.consent.shared.description')}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-800">
          {c.shared.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-slate-700">{t('sr.consent.notShared')}</p>
      </section>
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <Link className="font-medium text-brand underline" to={agreementPath(c.version)}>
          {t('sr.consent.agreementLink')}
        </Link>
        {c.agreement.draft ? <Badge tone="warn">{t('sr.consent.draftNotice')}</Badge> : null}
      </p>
      {c.privacyNoticeLines.length > 0 ? (
        <section aria-labelledby={`privacy-${version}`}>
          <h4 id={`privacy-${version}`} className="text-sm font-semibold text-slate-900">
            {t('srt.consent.privacy.title')}
          </h4>
          <p className="text-xs text-slate-600">{t('srt.consent.privacy.description')}</p>
          <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800">
            {c.privacyNoticeLines.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </div>
          <CopyLines lines={c.privacyNoticeLines} />
        </section>
      ) : null}
    </div>
  );
}
